import { Section, VStack } from 'tio-ui/components/layout';
import PageHeader, { PageHeaderCrumb } from 'tio-ui/components/page-header';
import type { WithBoundArgs } from '@glint/template';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { not, or } from 'tio-ui/utilities';
import * as v from 'valibot';
import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import type IntlService from 'ember-intl/services/intl';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import { query } from '@warp-drive/utilities/json-api';
import { saveExistingRecordWithChangelog } from 'tio-data/requests/save-record';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  isTasFieldComponentType,
} from '../../../utils/tuition-assistance/fields.ts';
import type { FieldName } from '../../../utils/tuition-assistance/fields.ts';
import { formatISODateToDisplay } from '../../../utils/date/index.ts';
import { trackedObject } from '@ember/reactive/collections';
import { dropTask } from 'ember-concurrency';
import type { Store } from '@warp-drive/core';
import type { CustomFieldSignature } from '../../../types/tuition-assistance.ts';
import { formatAsDollars } from '../../../utils/format.ts';
import lastFourDigits from '../../../helpers/last-four-digits.ts';
import Input from 'tio-ui/components/input';
import ReSelect from 'tio-ui/components/select';
import Textarea from 'tio-ui/components/textarea';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Drawer from 'tio-ui/components/drawer';
import Popover from 'tio-ui/components/popover';
import { PhPencil, PhBooks, PhMoneyWavy, PhCreditCard } from 'ember-phosphor-icons';
import { LinkTo } from '@ember/routing';
import TuitionAssistanceProgramFormField from '../../tuition-assistance/program-form-field.gts';
import SchoolSelect from '../../tuition-assistance/program-details/school-select.gts';
import TuitionAssistanceFormsCustomFieldsForProvince from '../../tuition-assistance/forms/custom-fields-for-province.gts';
import type { TasUnifiedInstitution as TasUnifiedInstitutionModel } from 'tio-data/data/resources/tas-unified-institution.type';
import type { TasUnifiedInstitution } from 'tio-data/data/resources/search.ext';

import ProgramApprovalFooter from './program-approval-footer.gts';
import CommentsDrawer from './drawers/comments.gts';
import PartnerInfoDrawer from './drawers/partner-info.gts';
import AttachmentsDrawer from './drawers/attachments.gts';
import ApplicationHistoryTimeline from '../application-history-timeline.gts';
import { DrawerHeader, DrawerBody } from 'tio-ui/components/drawer';
import ApprovalQueueStatusDrawer from '../approval/drawers/approval-queue-status.gts';

import type { ProgramDrawerType } from './types.ts';

/**
 * Configuration for editable program instance fields
 */
const PROGRAM_INSTANCE_FIELD_CONFIGS = [
  { name: 'SCHOOL_INSTITUTION_NAME', type: 'text', testId: 'school-name' },
  { name: 'PROGRAM_SPECIALIZATION', type: 'text', testId: 'specialization' },
  { name: 'PROGRAM_MAJOR', type: 'text', testId: 'major' },
  { name: 'ESTIMATED_PROGRAM_BEGIN', type: 'date', testId: 'program-begin' },
  { name: 'ESTIMATED_PROGRAM_COMPLETION', type: 'date', testId: 'program-completion' },
  { name: 'ESTIMATED_COST', type: 'currency', testId: 'estimated-cost' },
  { name: 'ENROLLMENT_OBJECTIVE', type: 'text', testId: 'enrollment-objective' },
] as const;

interface ProgramApprovalPageSignature {
  Element: HTMLElement;
  Args: {
    model: TasProgramInstanceModel;
    consumingApp?: string;
  };
  Blocks: {
    /** Breadcrumb trail rendered in the PageHeader breadcrumb row (route differs per app). Yields the `crumb` component. */
    breadcrumb: [crumb: WithBoundArgs<typeof PageHeaderCrumb, 'model'>];
    /** Status chips + program-id + employee/company metadata under the title. */
    meta: [];
    /** Right-aligned header actions (View link, document widget). */
    actions: [];
  };
}

export default class ProgramApprovalPage extends Component<ProgramApprovalPageSignature> {
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked selectedDrawerType: ProgramDrawerType | null = null;
  @tracked isDrawerOpen = false;

  // Inline program details editing
  @tracked isEditingProgramDetails = false;
  @tracked programFieldsCopy: TasProgramInstanceModel['fields'] | null = null;
  @tracked programCustomFieldsCopy: CustomFieldSignature[] | null = null;
  @tracked programDetailsChangelog = '';
  @tracked selectedSchool?: TasUnifiedInstitutionModel;
  @tracked hasSubmittedProgramDetails = false;

  // Comments drawer tab state
  @tracked commentsInitialTab: 'approver' | 'employee' = 'approver';

  // Validation

  get programDetailsChangelogValidationErrors(): string[] {
    if (!this.hasSubmittedProgramDetails) return [];
    const schema = v.pipe(
      v.string(),
      v.minLength(1, this.intl.t('tuition_assistance.program_details.comment_required'))
    );
    const result = v.safeParse(schema, this.programDetailsChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  get isProgramDetailsFormValid() {
    if (this.programDetailsChangelogValidationErrors.length > 0) return false;

    const template = this.args.model.tasProgramTemplate;
    const templateFields = template?.fields as Record<string, { required?: boolean }> | undefined;

    if (templateFields) {
      for (const fieldConfig of PROGRAM_INSTANCE_FIELD_CONFIGS) {
        const fieldMeta = templateFields[fieldConfig.name];
        if (fieldMeta?.required) {
          const value = this.getFieldValue(fieldConfig.name);
          if (!value || value === '') return false;
        }
      }
    }

    if (this.programCustomFieldsCopy) {
      for (const field of this.programCustomFieldsCopy) {
        if (field.required) {
          const hasValue =
            field.values &&
            field.values.length > 0 &&
            field.values[0] !== null &&
            field.values[0] !== undefined &&
            field.values[0] !== '';
          if (!hasValue) return false;
        }
      }
    }

    return true;
  }

  get customFieldValidationErrors(): { errors: string[]; value: string; path: string }[] {
    if (!this.hasSubmittedProgramDetails || !this.programCustomFieldsCopy) return [];

    const errors: { errors: string[]; value: string; path: string }[] = [];
    for (const field of this.programCustomFieldsCopy) {
      if (field.required) {
        const hasValue =
          field.values &&
          field.values.length > 0 &&
          field.values[0] !== null &&
          field.values[0] !== undefined &&
          field.values[0] !== '';
        if (!hasValue) {
          errors.push({
            path: field.label || field.name || '',
            value: '',
            errors: [
              this.intl.t('tuition_assistance.program_details.field_required', {
                fieldName: field.label || field.name,
              }),
            ],
          });
        }
      }
    }
    return errors;
  }

  // ── Drawer Management ──────────────────────────────────────────────────

  get isPartnerMessagingEnabled() {
    const companySetting = this.args.model.tasProgramTemplate?.company?.companySetting;
    return companySetting?.tuitionAssistance?.isPartnerMessagingEnabled ?? false;
  }

  openDrawer = (drawerType: ProgramDrawerType) => {
    if (this.selectedDrawerType === drawerType) {
      this.closeDrawer();
      return;
    }
    this.selectedDrawerType = drawerType;
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.selectedDrawerType = null;
    this.isDrawerOpen = false;
  };

  openCommentsDrawer = (tab: 'approver' | 'employee') => {
    this.commentsInitialTab = tab;
    this.openDrawer('comments');
  };

  // ── Program Details ────────────────────────────────────────────────────

  get programDetailRows() {
    const instance = this.args.model;
    const fields = instance.fields;
    const templateFields = instance.tasProgramTemplate?.fields as
      Record<string, { visible?: boolean; label?: string }> | undefined;
    const rows: { label: string; value: string }[] = [];

    for (const config of PROGRAM_INSTANCE_FIELD_CONFIGS) {
      const templateField = templateFields?.[config.name];
      if (templateField?.visible === false) continue;

      const label = templateField?.label || config.name.replace(/_/g, ' ');
      let value = getSingleValueForTasField(config.name as FieldName, fields) as string;

      if (config.type === 'date' && value) {
        value = formatISODateToDisplay(value, this.intl);
      } else if (config.type === 'currency' && value) {
        value = formatAsDollars(Number(value) || 0);
      }

      rows.push({ label, value: value || 'N/A' });
    }

    return rows;
  }

  get visibleEmployeeCustomFields() {
    return (this.args.model.customFields || [])
      .filter((field) => field.province === 'EMPLOYEE' && field.visible !== false)
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0))
      .map((field) => ({ ...field, displayValue: field.values?.[0] || 'N/A' }));
  }

  get visibleProgramCustomFields() {
    return (this.args.model.customFields || [])
      .filter((field) => field.province === 'PROGRAM' && field.visible !== false)
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0))
      .map((field) => ({ ...field, displayValue: field.values?.[0] || 'N/A' }));
  }

  get employeeCustomFieldsCopy() {
    return (this.programCustomFieldsCopy || [])
      .filter((field) => field.province === 'EMPLOYEE')
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
  }

  get programOnlyCustomFieldsCopy() {
    return (this.programCustomFieldsCopy || [])
      .filter((field) => field.province === 'PROGRAM')
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
  }

  get safeCustomFieldsCopy() {
    return this.programCustomFieldsCopy || [];
  }

  get canEditProgramDetails() {
    return this.args.model.approverCanEditApplication;
  }

  // ── Inline Editing ─────────────────────────────────────────────────────

  startEditingProgramDetails = async () => {
    this.programFieldsCopy = trackedObject(JSON.parse(JSON.stringify(this.args.model.fields)));
    this.programCustomFieldsCopy = JSON.parse(JSON.stringify(this.args.model.customFields || []));
    this.programDetailsChangelog = '';

    // Load previously selected school from the store (matches approval-page pattern)
    try {
      const tasInstitutionId = this.args.model.fields['TAS_INSTITUTION_ID']?.values?.[0];
      const dapipInstitutionId = this.args.model.fields['DAPIP_INSTITUTION_ID']?.values?.[0];

      if (tasInstitutionId || dapipInstitutionId) {
        const filter: Record<string, string | number | undefined> = {
          'tas-program-template': this.args.model.tasProgramTemplate?.id ?? undefined,
        };

        if (dapipInstitutionId) {
          filter['dapip-id'] = dapipInstitutionId;
        }

        if (tasInstitutionId) {
          filter['institution-id'] = tasInstitutionId;
        }

        // WarpDrive builder read (Phase 2.4) — replaces store.query;
        // flattened bracket keys satisfy the builder's QueryParamsSource type.
        const queryParams: Record<string, string | number> = {
          'page[limit]': 1,
          'page[offset]': 0,
        };
        if (filter['tas-program-template'] != null) {
          queryParams['filter[tas-program-template]'] = filter['tas-program-template'] as
            string | number;
        }
        if (filter['dapip-id'] != null) {
          queryParams['filter[dapip-id]'] = filter['dapip-id'] as string | number;
        }
        if (filter['institution-id'] != null) {
          queryParams['filter[institution-id]'] = filter['institution-id'] as string | number;
        }

        const { content } = (await this.store.request(
          query<TasUnifiedInstitutionModel>('tas-unified-institution', queryParams)
        )) as { content: { data: TasUnifiedInstitutionModel[] } };
        const results = content.data;

        if (results.length > 0) {
          this.selectedSchool = results[0];
        }
      }
    } catch (e) {
      console.error('Error attempting to load previously selected school', e);
    }

    this.isEditingProgramDetails = true;
  };

  cancelEditingProgramDetails = () => {
    this.isEditingProgramDetails = false;
    this.programFieldsCopy = null;
    this.programCustomFieldsCopy = null;
    this.programDetailsChangelog = '';
    this.selectedSchool = undefined;
    this.hasSubmittedProgramDetails = false;
  };

  updateProgramField = (fieldName: string, value: string) => {
    if (!this.programFieldsCopy) return;
    // @ts-expect-error - Dynamic field name indexing
    setSingleValueForTasField(fieldName, value, this.programFieldsCopy);
  };

  updateCustomFields = (fields: CustomFieldSignature[], _key?: string) => {
    this.programCustomFieldsCopy = fields;
  };

  getFieldValue = (fieldName: string) => {
    if (!this.programFieldsCopy) return '';
    return (
      (getSingleValueForTasField(
        fieldName as FieldName,
        this.programFieldsCopy as Parameters<typeof getSingleValueForTasField>[1]
      ) as string) || ''
    );
  };

  // ── School Select ──────────────────────────────────────────────────────

  isSchoolSelectField = (fieldName: string) => {
    const programTemplate = this.args.model.tasProgramTemplate;
    if (!programTemplate?.fields) {
      return fieldName === 'SCHOOL_INSTITUTION_NAME';
    }
    return isTasFieldComponentType(
      fieldName as Parameters<typeof isTasFieldComponentType>[0],
      programTemplate.fields,
      'school-select'
    );
  };

  didSelectSchool = (searchResult?: TasUnifiedInstitution) => {
    this.selectedSchool = searchResult as unknown as TasUnifiedInstitutionModel;

    if (!searchResult || !this.programFieldsCopy) {
      return;
    }

    setSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      searchResult.locationName || '',
      this.programFieldsCopy
    );

    setSingleValueForTasField(
      'TAS_INSTITUTION_ID',
      searchResult.institutionId || '',
      this.programFieldsCopy
    );

    setSingleValueForTasField(
      'DAPIP_INSTITUTION_ID',
      searchResult.dapipId || '',
      this.programFieldsCopy
    );
  };

  saveProgramDetailsTask = dropTask(async () => {
    this.hasSubmittedProgramDetails = true;

    if (!this.isProgramDetailsFormValid || !this.programFieldsCopy) return;

    this.args.model.fields = this.programFieldsCopy as TasProgramInstanceModel['fields'];
    if (this.programCustomFieldsCopy) {
      this.args.model.customFields = this.programCustomFieldsCopy;
    }

    // WarpDrive mutation builder (Phase 2.7) — replaces
    // model.save({ adapterOptions: { changelog } }); the changelog rides as
    // data.attributes._changelog on the PATCH (ApplicationSerializer parity).
    await saveExistingRecordWithChangelog(
      this.store,
      this.args.model,
      this.programDetailsChangelog
    );
    this.isEditingProgramDetails = false;
    this.programFieldsCopy = null;
    this.programCustomFieldsCopy = null;
    this.programDetailsChangelog = '';
    this.selectedSchool = undefined;
    this.hasSubmittedProgramDetails = false;
  });

  // ── Child Applications ─────────────────────────────────────────────────

  get activeApplications() {
    return (this.args.model.tasApplications || []).filter(
      (app: TasApplicationModel) => app.state !== 'TAS.ApplicationState.ABANDONED'
    );
  }

  // ── Timeline ───────────────────────────────────────────────────────────

  get timelineHistory() {
    return this.args.model.fullHistory || [];
  }

  // ── Approval Phase Info ────────────────────────────────────────────────

  get approvalPhaseLabel() {
    const state = this.args.model.state;
    if (
      state.includes('PROGRAM_APPROVAL') ||
      state.includes('PROGRAM_APPROVED') ||
      state.includes('PROGRAM_NOT_APPROVED')
    ) {
      return 'Program Approval';
    }
    if (state.includes('COMPLETION')) {
      return 'Completion Approval';
    }
    if (state.includes('WITHDRAWAL')) {
      return 'Withdrawal Approval';
    }
    if (state.includes('PRIMED')) {
      return '';
    }
    if (state.includes('COMPLETED')) {
      return 'Completed';
    }
    return '';
  }

  // ── Partner Info ───────────────────────────────────────────────────────

  get approverInstructionsItems() {
    const template = this.args.model.tasProgramTemplate;
    if (!template) return [];

    const instructions = template.programApproverInstructions || [];
    return instructions.map((instruction) => ({
      heading: instruction.heading || `Instruction ${instruction.ordinal}`,
      body: instruction.body,
      ordinal: instruction.ordinal,
    }));
  }

  get employeeFaqsItems() {
    const template = this.args.model.tasProgramTemplate;
    if (!template) return [];

    const faqs = template.programFaqs || [];
    return faqs.map(
      (faq: {
        heading?: string;
        question?: string;
        body?: string;
        answer?: string;
        ordinal?: number;
      }) => ({
        heading: faq.heading || faq.question || 'FAQ',
        body: faq.body || faq.answer || '',
        ordinal: faq.ordinal || 0,
      })
    );
  }

  // ── Attachments ────────────────────────────────────────────────────────

  get isEmployeeApp() {
    return this.args.consumingApp === 'tio-employee';
  }

  get applicationApprovalRoute() {
    return this.isEmployeeApp
      ? 'authenticated.admin.tas.applications.approval'
      : 'authenticated.tas.applications.approval';
  }

  get allAttachments() {
    const instanceAssets = this.args.model.tasAssets || [];
    const applicationAssets = this.args.model.tasApplications.flatMap((app) => app.tasAssets || []);
    const courseAssets = this.args.model.tasApplications
      .flatMap((app) => app.tasCourses || [])
      .flatMap((course) => course.tasAssets || []);
    return [...instanceAssets, ...applicationAssets, ...courseAssets];
  }

  <template>
    {{pageTitle (t "tuition_assistance.program_details.default")}}

    <VStack class="min-h-screen" ...attributes>
      <PageHeader @title={{@model.programName}}>
        <:breadcrumbs as |b|>
          {{yield b.crumb to="breadcrumb"}}
        </:breadcrumbs>
        <:actions>
          {{yield to="actions"}}
        </:actions>
        <:below>
          {{yield to="meta"}}
        </:below>
      </PageHeader>

      <Section>
        <div class="flex-1 pb-32">
          <div class="max-w-7xl mx-auto">
            <VStack @collapsed={{true}}>
              {{! Program Details Section }}
              <Section>
                <:header>
                  <div
                    class="tio-row-between px-4 py-2.5 tio-bg-sunken-translucent border tio-border-default-translucent rounded-[10px] max-w-3xl"
                  >
                    <h3 class="tio-text-base-semibold tio-fg-primary">
                      {{t "tuition_assistance.program_details.default"}}
                    </h3>
                    {{#unless this.isEditingProgramDetails}}
                      {{#if this.canEditProgramDetails}}
                        <Button
                          @appearance="minimal"
                          @size="sm"
                          @icon={{PhPencil}}
                          {{on "click" this.startEditingProgramDetails}}
                          data-test-edit-program-details
                        />
                      {{/if}}
                    {{/unless}}
                  </div>
                </:header>
                <:body>
                  {{#if this.isEditingProgramDetails}}
                    {{! Edit Mode }}
                    <div class="tio-col-loose p-4 max-w-3xl">
                      {{#each PROGRAM_INSTANCE_FIELD_CONFIGS as |fieldConfig|}}
                        {{#if (this.isSchoolSelectField fieldConfig.name)}}
                          <SchoolSelect
                            @programTemplate={{@model.tasProgramTemplate}}
                            @selected={{this.selectedSchool}}
                            @onChange={{this.didSelectSchool}}
                            data-test={{fieldConfig.testId}}
                          />
                        {{else}}
                          <TuitionAssistanceProgramFormField
                            @name={{fieldConfig.name}}
                            @programTemplate={{@model.tasProgramTemplate}}
                            as |field|
                          >
                            {{#if field.isFieldOptionPicklist}}
                              <ReSelect
                                data-test={{fieldConfig.testId}}
                                @items={{field.fieldOptions}}
                                @selected={{this.getFieldValue fieldConfig.name}}
                                @onChange={{fn this.updateProgramField fieldConfig.name}}
                                @size="sm"
                              />
                            {{else}}
                              <Input
                                data-test-field={{fieldConfig.testId}}
                                id={{field.inputId}}
                                @value={{this.getFieldValue fieldConfig.name}}
                                @onChange={{fn this.updateProgramField fieldConfig.name}}
                                @size="sm"
                              />
                            {{/if}}
                          </TuitionAssistanceProgramFormField>
                        {{/if}}
                      {{/each}}

                      {{! Employee Custom Fields }}
                      {{#if this.employeeCustomFieldsCopy.length}}
                        <TuitionAssistanceFormsCustomFieldsForProvince
                          @customFields={{this.safeCustomFieldsCopy}}
                          @didUpdateFields={{this.updateCustomFields}}
                          @province="EMPLOYEE"
                          @formValidationErrors={{this.customFieldValidationErrors}}
                        />
                      {{/if}}

                      {{! Program Custom Fields }}
                      {{#if this.programOnlyCustomFieldsCopy.length}}
                        <TuitionAssistanceFormsCustomFieldsForProvince
                          @customFields={{this.safeCustomFieldsCopy}}
                          @didUpdateFields={{this.updateCustomFields}}
                          @province="PROGRAM"
                          @formValidationErrors={{this.customFieldValidationErrors}}
                        />
                      {{/if}}

                      {{! Changelog }}
                      <div>
                        <label class="flex tio-text-sm-semibold tio-fg-secondary mb-1">
                          Comment
                          <span class="tio-fg-danger ml-1">*</span>
                        </label>
                        <Textarea
                          @value={{this.programDetailsChangelog}}
                          @onChange={{fn (mut this.programDetailsChangelog)}}
                          @errors={{this.programDetailsChangelogValidationErrors}}
                          @size="sm"
                          placeholder="Describe what you changed and why..."
                          data-test-program-details-changelog
                        />
                      </div>

                      <div class="tio-flex-tight justify-end">
                        <Button
                          @appearance="outlined"
                          {{on "click" this.cancelEditingProgramDetails}}
                        >
                          {{t "common.cancel"}}
                        </Button>
                        <Button
                          @intent="primary"
                          @isRunning={{this.saveProgramDetailsTask.isRunning}}
                          {{on "click" this.saveProgramDetailsTask.perform}}
                          @isDisabled={{or
                            this.saveProgramDetailsTask.isRunning
                            (not this.isProgramDetailsFormValid)
                          }}
                        >
                          {{if this.saveProgramDetailsTask.isRunning "Saving..." "Save Changes"}}
                        </Button>
                      </div>
                    </div>
                  {{else}}
                    {{! Read-only Mode }}
                    <div class="divide-y tio-divide-default max-w-3xl">
                      {{#each this.programDetailRows as |row|}}
                        <div class="flex py-4 px-4">
                          <div
                            class="w-56 shrink-0 tio-text-sm-medium tio-fg-secondary"
                          >{{row.label}}</div>
                          <span
                            class="tio-text-sm-medium tio-fg-secondary px-2"
                          >{{row.value}}</span>
                        </div>
                      {{/each}}
                      {{#each this.visibleEmployeeCustomFields as |field|}}
                        <div class="flex py-4 px-4">
                          <div
                            class="w-56 shrink-0 tio-text-sm-medium tio-fg-secondary"
                          >{{field.label}}</div>
                          <span class="tio-text-sm-medium tio-fg-secondary px-2">
                            {{field.displayValue}}
                          </span>
                        </div>
                      {{/each}}
                      {{#each this.visibleProgramCustomFields as |field|}}
                        <div class="flex py-4 px-4">
                          <div
                            class="w-56 shrink-0 tio-text-sm-medium tio-fg-secondary"
                          >{{field.label}}</div>
                          <span class="tio-text-sm-medium tio-fg-secondary px-2">
                            {{field.displayValue}}
                          </span>
                        </div>
                      {{/each}}
                    </div>
                  {{/if}}
                </:body>
              </Section>

              {{! Child Applications Section }}
              <Section>
                <:header>
                  <div
                    class="tio-row-between px-4 py-2.5 tio-bg-sunken-translucent border tio-border-default-translucent rounded-[10px] max-w-3xl"
                  >
                    <h3 class="tio-text-base-semibold tio-fg-primary">
                      Applications ({{this.activeApplications.length}})
                    </h3>
                  </div>
                </:header>
                <:body>
                  {{#if this.activeApplications.length}}
                    <div class="space-y-2 max-w-3xl">
                      {{#each this.activeApplications as |app|}}
                        <div
                          class="relative isolate block rounded-2xl border tio-border-default tio-bg-surface p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <LinkTo
                            @route={{this.applicationApprovalRoute}}
                            @model={{app.id}}
                            aria-label={{app.displayName}}
                            class="tio-program-approval__card-target absolute inset-0 z-0 rounded-2xl"
                          >
                            <span class="sr-only">{{app.displayName}}</span>
                          </LinkTo>
                          <div class="relative z-10 pointer-events-none tio-row">
                            <div
                              class="tio-row-center app-icon-circle--brand h-12 w-12 shrink-0 rounded-lg"
                            >
                              <PhBooks class="h-7 w-7" />
                            </div>
                            <div class="tio-col-extra-tight tio-fill-min">
                              <div class="tio-row-tight flex-wrap">
                                <span class="tio-text-sm-semibold tio-fg-primary truncate">
                                  {{app.displayName}}
                                </span>
                                <Chip @intent={{app.badgeIntent}} @size="sm">
                                  {{app.badgeLabel}}
                                </Chip>
                              </div>
                              <span class="tio-text-caption">
                                <span>ID# xx{{lastFourDigits app.id}}</span>
                                {{#if app.activeCourses.length}}
                                  <span class="mx-1">&bull;</span>
                                  <Popover
                                    @placement="bottom-start"
                                    @triggerMode="click"
                                    @maxWidth={{360}}
                                  >
                                    <:trigger as |pop|>
                                      <button
                                        type="button"
                                        class="tio-inline-row self-start cursor-pointer pointer-events-auto"
                                        aria-haspopup="dialog"
                                        aria-expanded={{if pop.isOpen "true" "false"}}
                                        aria-controls={{pop.panelId}}
                                        {{pop.register}}
                                        {{on "click" pop.toggle}}
                                      >
                                        <span
                                          class="underline underline-offset-2 tio-decoration-muted hover:tio-fg-secondary"
                                        >
                                          {{app.activeCourses.length}}
                                          {{if (eq app.activeCourses.length 1) "Course" "Courses"}}
                                        </span>
                                      </button>
                                    </:trigger>
                                    <:body>
                                      <div class="p-3 text-sm min-w-64">
                                        {{#if app.activeCourses.length}}
                                          <ul class="space-y-1">
                                            {{#each app.activeCourses as |course|}}
                                              <li class="tio-fg-secondary">
                                                <span class="font-medium">
                                                  {{if
                                                    course.courseNumber
                                                    course.courseNumber
                                                    "Course"
                                                  }}
                                                </span>:
                                                {{if
                                                  course.courseName
                                                  course.courseName
                                                  "Unnamed Course"
                                                }}
                                              </li>
                                            {{/each}}
                                          </ul>
                                        {{else}}
                                          <p class="tio-fg-secondary">No active courses</p>
                                        {{/if}}
                                      </div>
                                    </:body>
                                  </Popover>
                                {{/if}}
                                {{#if app.requestedTotal}}
                                  <span class="mx-1">&bull;</span>
                                  <span>{{formatAsDollars app.requestedTotal}}</span>
                                {{/if}}
                              </span>
                            </div>
                            {{! Right-side metrics }}
                            <div class="hidden shrink-0 gap-2 md:flex items-center">
                              {{#let
                                app.approvedTotal app.paidTotal app.requestedTotal
                                as |approved paid requested|
                              }}
                                {{#let
                                  (if paid paid (if approved approved requested))
                                  as |reimbursementAmount|
                                }}
                                  {{#if (gt reimbursementAmount 0)}}
                                    <div
                                      class="app-border-brand rounded-md border px-2 py-0.5 text-left leading-tight"
                                    >
                                      <span
                                        class="tio-row-flush gap-0.5 tio-text-micro font-medium tio-fg-secondary"
                                      >
                                        Reimbursement
                                        <PhMoneyWavy class="h-4 w-4" />
                                      </span>
                                      <div class="tio-text-xs-bold tio-fg-primary">
                                        {{formatAsDollars reimbursementAmount}}
                                      </div>
                                      <span class="tio-text-micro app-text-brand-strong">
                                        {{if paid "paid" (if approved "approved" "requested")}}
                                      </span>
                                    </div>
                                  {{/if}}
                                {{/let}}
                              {{/let}}
                              {{#let app.amounts as |amounts|}}
                                {{#if amounts}}
                                  {{#let
                                    (if
                                      amounts.course_cash.used
                                      amounts.course_cash.used
                                      (if
                                        amounts.course_cash.reserved amounts.course_cash.reserved 0
                                      )
                                    )
                                    as |ccAmount|
                                  }}
                                    {{#if (gt ccAmount 0)}}
                                      <div
                                        class="app-border-brand rounded-md border px-2 py-0.5 text-left leading-tight"
                                      >
                                        <span
                                          class="tio-row-flush gap-0.5 tio-text-micro font-medium tio-fg-secondary"
                                        >
                                          {{t "common.tas.course_cash.default" htmlSafe=true}}
                                          <PhCreditCard class="h-4 w-4" />
                                        </span>
                                        <div class="tio-text-xs-bold tio-fg-primary">
                                          {{formatAsDollars ccAmount}}
                                        </div>
                                        <span class="tio-text-micro app-text-brand-strong">
                                          {{if amounts.course_cash.used "used" "reserved"}}
                                        </span>
                                      </div>
                                    {{/if}}
                                  {{/let}}
                                {{/if}}
                              {{/let}}
                            </div>
                          </div>
                        </div>
                      {{/each}}
                    </div>
                  {{else}}
                    <p class="text-sm tio-fg-muted p-4 max-w-3xl">
                      {{t "tuition_assistance.program_details.no_applications"}}
                    </p>
                  {{/if}}
                </:body>
              </Section>
            </VStack>
          </div>
        </div>

        {{! Sticky Footer }}
        <ProgramApprovalFooter
          @instance={{@model}}
          @onDrawerOpen={{this.openDrawer}}
          @activeDrawer={{this.selectedDrawerType}}
          @consumingApp={{@consumingApp}}
        />
      </Section>

      {{! Drawer }}
      <Drawer
        @isOpen={{this.isDrawerOpen}}
        @onClose={{this.closeDrawer}}
        @placement="right"
        @size="xl"
      >
        {{#if (eq this.selectedDrawerType "comments")}}
          <CommentsDrawer
            @instance={{@model}}
            @initialTab={{this.commentsInitialTab}}
            @showEmployeeTab={{this.isPartnerMessagingEnabled}}
          />
        {{else if (eq this.selectedDrawerType "partner-info")}}
          <PartnerInfoDrawer
            @instance={{@model}}
            @approverInstructionsItems={{this.approverInstructionsItems}}
            @employeeFaqsItems={{this.employeeFaqsItems}}
            @isAdminApp={{not this.isEmployeeApp}}
          />
        {{else if (eq this.selectedDrawerType "attachments")}}
          <AttachmentsDrawer
            @instance={{@model}}
            @allAttachments={{this.allAttachments}}
            @hideDiscarded={{this.isEmployeeApp}}
          />
        {{else if (eq this.selectedDrawerType "timeline")}}
          <DrawerHeader>
            <h2 class="tio-text-heading tio-fg-primary">{{t
                "tuition_assistance.application_approval.timeline"
              }}</h2>
          </DrawerHeader>
          <DrawerBody>
            <ApplicationHistoryTimeline
              @history={{this.timelineHistory}}
              @title={{t "tuition_assistance.program_instances.history.header"}}
              @translationPrefix="tuition_assistance.program_instances.history"
              @embedded={{true}}
            />
          </DrawerBody>
        {{else if (eq this.selectedDrawerType "approval-queue")}}
          {{! @glint-expect-error: consumingApp is optional on this page but required on drawer }}
          <ApprovalQueueStatusDrawer @application={{@model}} @consumingApp={{@consumingApp}} />
        {{/if}}
      </Drawer>
    </VStack>
  </template>
}
